import type { POLICY_FILES } from '@/constants';
import type {
  GetAdminNoticesMainParams,
  GetAdminProductOrdersParams,
  GetAdminProductsParams,
  GetAdminUsersParams,
  GetOrderNoteLogsParams,
  GetOrderStatusLogsParams,
} from '@/types/admin';
import type {
  PaginationRequestParams,
  PathVariableRequestParams,
  StatisticsPathVariableRequestParams,
} from '@/types/api';
import type {
  GetBranchDailyOperationRatesParams,
  GetBranchDailySharesParams,
  GetContractsParams,
} from '@/types/universal';

const getOptionalParamsQueryKey = (mainKey: string, params?: unknown) => {
  return params ? [mainKey, params] : [mainKey];
};

const queryKeys = {
  universal: {
    getMe: () => getOptionalParamsQueryKey('getMe'),
    getMyPermissions: () => getOptionalParamsQueryKey('getMyPermissions'),
    getBranchDailyShares: (params?: GetBranchDailySharesParams) =>
      getOptionalParamsQueryKey('getBranchDailyShares', params),
    getBranchDailyOperationRates: (
      params?: GetBranchDailyOperationRatesParams
    ) => getOptionalParamsQueryKey('getBranchDailyOperationRates', params),
    getUnitOperationRates: (params?: StatisticsPathVariableRequestParams) =>
      getOptionalParamsQueryKey('getUnitOperationRates', params),
    getUnitCompositionRates: (params?: StatisticsPathVariableRequestParams) =>
      getOptionalParamsQueryKey('getUnitCompositionRates', params),
    getAdjustments: () => getOptionalParamsQueryKey('getAdjustments'),
    getContracts: (params?: GetContractsParams) =>
      getOptionalParamsQueryKey('getContracts', params),
  },
  partner: {
    product: {
      getProducts: (params?: PaginationRequestParams) =>
        getOptionalParamsQueryKey('getProducts', params),
      getProduct: (params?: PathVariableRequestParams) =>
        getOptionalParamsQueryKey('getProduct', params),
      getProductOrders: (params?: PaginationRequestParams) =>
        getOptionalParamsQueryKey('getProductOrders', params),
      getProductOrder: (params?: PathVariableRequestParams) =>
        getOptionalParamsQueryKey('getProductOrder', params),
    },
    notice: {
      getNoticesMain: (params?: PaginationRequestParams) =>
        getOptionalParamsQueryKey('getPartnerNoticesMain', params),
      getNotice: (params?: PathVariableRequestParams) =>
        getOptionalParamsQueryKey('getPartnerNotice', params),
    },
  },
  admin: {
    getAllBranches: () => getOptionalParamsQueryKey('getAllBranches'),
    notice: {
      getNoticesMain: (params?: GetAdminNoticesMainParams) =>
        getOptionalParamsQueryKey('admin:getNoticesMain', params),
      getNotice: (params?: PathVariableRequestParams) =>
        getOptionalParamsQueryKey('getAdminNotice', params),
      getNoticeAudiences: (params?: PathVariableRequestParams) =>
        getOptionalParamsQueryKey('admin:getNoticeAudiences', params),
    },
    product: {
      getProducts: (params?: GetAdminProductsParams) =>
        getOptionalParamsQueryKey('getAdminProducts', params),
      getProduct: (params?: PathVariableRequestParams) =>
        getOptionalParamsQueryKey('getAdminProduct', params),
      getProductOrders: (params?: GetAdminProductOrdersParams) =>
        getOptionalParamsQueryKey('getAdminProductOrders', params),
      getProductOrder: (params?: PathVariableRequestParams) =>
        getOptionalParamsQueryKey('getAdminProductOrder', params),
      getOrderStatusLogs: (params?: GetOrderStatusLogsParams) =>
        getOptionalParamsQueryKey('getOrderStatusLogs', params),
      getOrderNoteLogs: (params?: GetOrderNoteLogsParams) =>
        getOptionalParamsQueryKey('getOrderNoteLogs', params),
    },
  },
  external: {
    policy: {
      getPolicy: (key: string) => getOptionalParamsQueryKey('getPolicy', key),
    },
  },
};

type Params<Key extends string, Params = never> = {
  key: Key;
  params?: Params;
};

type GetQueryKeyParams =
  | Params<'getMe'> // 내 정보
  | Params<'getMyPermissions'> // 내 메뉴 권한
  | Params<'getContracts', GetContractsParams> // 판매 현황
  | Params<'getBranchDailyShares', GetBranchDailySharesParams> // 지점 점유율 - 날짜별 점유율
  | Params<'getBranchDailyOperationRates', GetBranchDailyOperationRatesParams> // 지점 점유율 - 날짜별 가동율
  | Params<'getUnitOperationRates', StatisticsPathVariableRequestParams> // 지점 점유율 - 유닛별 가동율
  | Params<'getUnitCompositionRates', StatisticsPathVariableRequestParams> // 지점 점유율 - 유닛 구성
  | Params<'getAdjustments'> // 정산 내역
  // 파트너+관리자 전용
  | Params<'getProducts', PaginationRequestParams> // 마케팅 상품 - 상품 목록
  | Params<'getProduct', PathVariableRequestParams> // 마케팅 상품 - 상품 상세
  | Params<'getProductOrders', PaginationRequestParams> // 마케팅 상품 - 마케팅 상품 신청 내역 목록
  | Params<'getProductOrder', PathVariableRequestParams> // 마케팅 상품 - 마케팅 상품 신청 내역 상세
  | Params<'getPartnerNoticesMain', PaginationRequestParams> // 파트너 - 공지사항 메인 조회
  | Params<'getPartnerNotice', PathVariableRequestParams> // 파트너 - 공지사항 상세
  // 관리자 전용
  | Params<'admin:getAllBranches'> // 모든 지점 목록
  | Params<'admin:getNoticesMain', GetAdminNoticesMainParams> // 관리자 - 공지사항 메인 조회
  | Params<'admin:getNotice', PathVariableRequestParams> // 관리자 - 공지사항 상세
  | Params<'admin:getNoticeAudiences', PathVariableRequestParams> // 관리자 - 공지사항 대상자 리스트
  | Params<'admin:getProducts', GetAdminProductsParams> // 관리자 - 마케팅 상품 목록
  | Params<'admin:getProduct', PathVariableRequestParams> // 관리자 - 마케팅 상품 상세
  | Params<'admin:getProductOrders', GetAdminProductOrdersParams> // 관리자 - 마케팅 상품 신청 내역 목록
  | Params<'admin:getProductOrder', PathVariableRequestParams> // 관리자 - 마케팅 상품 신청 내역 상세
  | Params<'admin:getOrderStatusLogs', GetOrderStatusLogsParams> // 관리자 - 마케팅 상품 신청 내역 상세 - 상태 변경 내역
  | Params<'admin:getOrderNoteLogs', GetOrderNoteLogsParams> // 관리자 - 마케팅 상품 신청 내역 상세 - 내부용 메모 변경 내역
  | Params<'admin:getUsers', GetAdminUsersParams> // 관리자 - 유저 목록
  | Params<'admin:getUser', PathVariableRequestParams> // 관리자 - 유저 상세
  | Params<'admin:getUserRoles'> // 관리자 - 유저 역할 목록
  | Params<'admin:getPermissions'> // 관리자 - 매핑 가능한 전체 권한
  // 외부
  | Params<'external:getPolicy', keyof typeof POLICY_FILES>;

export const getQueryKey = <K extends GetQueryKeyParams['key']>(
  key: K,
  params?: Extract<GetQueryKeyParams, { key: K }>['params']
) => {
  return params ? [key, params] : [key];
};

export default queryKeys;
